import { ICommandBarItemProps } from "@fluentui/react"
import type {
    IPartialTheme,
    IProcessedStyleSet,
    IStyle,
    IStyleSet
} from "@fluentui/react/lib/Styling"
import { IPropsWithStyles } from "@fluentui/react/lib/Utilities"
import { Bus } from "baconjs"
import { ForwardedRef } from "react"

import { ConfigurationService } from "@encoway/c-services-js-client"

import {
    IConfig as IConfigurationServiceConfig,
    IConfigurationSettings
} from "../../configurationService.types"
import {
    IVisualEditor,
    IVisualizationSettings
} from "../../services/visualization/visualizationService.types"

export type IVisualizationWrapperStyles = {
    /**
     * Styles for the container with the spinner, the error message of the visualization not loading and the retry button.
     */
    centered?: IStyle
    /**
     * Styles for the info message displayed in case the visualization didn't load.
     */
    infoText?: IStyle
    /**
     * Styles for the retry button displayed in case the visualization didn't load.
     */
    retryButton?: IStyle
}

export interface ISaveData {
    images?: any
    assemblyState?: any
    bom?: any
    readyState?: string
}

export enum VisualizationDataSaveEvents {
    SaveRequest,
    SaveFinished,
    SaveIncomplete
}

export interface ISaveResult {
    event: VisualizationDataSaveEvents
    data: ISaveData
    message?: string
}

export interface ICfg {
    configuration?: IConfigurationServiceConfig
}

export interface IVisualizationWrapperProps
    extends IPropsWithStyles<
        IPartialTheme,
        IStyleSet<IVisualizationWrapperStyles>
    > {
    /**
     * Used for partially overriding the globally set theme and determining the theme used by the component for its presentation.
     */
    theme?: IPartialTheme
    /**
     * Available class names for styling.
     */
    classNames?: IProcessedStyleSet<IVisualizationWrapperStyles>
    /**
     * Optional argument to forward the ref to use for the Visualization component.
     */
    visualEditorRef: ForwardedRef<IVisualEditor>
    /**
     * Stores the settings needed for the visualization.
     */
    settings?: IVisualizationSettings
    /**
     * Stores the current state of the visualization component.
     */
    visualizationState?: object
    /**
     * Stores the settings needed for the configuration such as the baseUrl and jsessionid.
     */
    configurationSettings: IConfigurationSettings
    /**
     * The configuration service client instance from `@encoway/c-services-js-client`.
     */
    configurationService?: ConfigurationService
    /**
     * Alternative implementation of the `@encoway/react-configurator` instance which will be used instead.
     */
    reactConfigurator?: typeof import("@encoway/react-configurator")
    /**
     *  Provides the initialization data needed for the visualization.
     */
    visualizationProvider?: any
    /**
     * Defines whether the user can edit (false) or view (true) the visualization.
     */
    readOnly?: boolean
    /**
     * Shows the Busy Overlay on the screen if the input parameter is true.
     */
    setBusyOverlay?: (f: boolean) => void
    /**
     * (Optional) function to build custom context menus within the visualization.
     */
    onContextMenu?: (
        node: object,
        dismissMenu: () => void,
        contextAnchor: object | null
    ) => { replace: boolean; items: ICommandBarItemProps[] }
    /**
     * The bacon bus.
     * This is required for syncing the visualization with the configurator state.
     */
    eventBus: Bus<any>
}
